import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.addFormValidation()
    this.ensureSubmitButtons()
    this.debugFormSubmission()
    this.setupFormRecovery()
  }

  setupFormRecovery() {
    const monthlyRevenueField = document.getElementById('monthly_revenue_field')
    if (monthlyRevenueField) {
      monthlyRevenueField.addEventListener('change', (event) => {
        if (event.isPlaidUpdate === true) {
          setTimeout(() => this.verifyFormIntegrity(), 100)
        }
        this.enableAllSubmitButtons()
      })
    }

    this.integrityCheckInterval = setInterval(() => {
      this.verifyFormIntegrity()
    }, 5000)
  }

  verifyFormIntegrity() {
    const form = this.element

    if (form.getAttribute("aria-busy") === "true") return

    const submitButtons = form.querySelectorAll('input[type="submit"], button[type="submit"]')

    submitButtons.forEach(button => {
      if (!button.form || button.form !== form) {
        if (!form.contains(button)) {
          console.error('Submit button is no longer inside the form - this indicates DOM structure damage')
        }
      }

      if (button.dataset.managedDisabled === "true" || button.id === "verify-continue-button") {
        return
      }

      if (button.disabled) {
        button.disabled = false
      }
    })

    if (!this.formHasSubmitListener) {
      this.addFormValidation()
    }
  }

  disconnect() {
    if (this.integrityCheckInterval) {
      clearInterval(this.integrityCheckInterval)
    }
  }

  enableAllSubmitButtons() {
    const submitButtons = this.element.querySelectorAll('input[type="submit"], button[type="submit"]')
    submitButtons.forEach(button => {
      if (button.dataset.managedDisabled === "true" || button.id === "verify-continue-button") {
        return
      }
      button.disabled = false
    })
  }

  addFormValidation() {
    const form = this.element
    if (form && form.tagName === 'FORM') {
      form.addEventListener('submit', this.validateForm.bind(this))
      this.formHasSubmitListener = true
    }
  }

  validateForm(event) {
    const form = event.target

    const autoSaveInput = form.querySelector('input[name="auto_save"]')
    if (autoSaveInput && autoSaveInput.value === 'true') {
      return true
    }

    // Skip validation when navigating backwards — server saves without validation
    const goingBackInput = form.querySelector('input[name="going_backwards"]')
    if (goingBackInput && goingBackInput.value === 'true') {
      return true
    }

    const isValid = this._performValidation(form)

    if (!isValid) {
      event.preventDefault()
      this.showValidationMessage()
      return false
    }

    return true
  }

  _performValidation(form) {
    const currentStep = form.querySelector('input[name="application[current_step]"]')?.value
    const visibleRequiredFields = form.querySelectorAll('[required]:not([style*="display: none"]):not(.hidden)')
    let isValid = true

    visibleRequiredFields.forEach(field => {
      field.classList.remove('border-red-500')
      this.clearFieldError(field)
    })

    if (currentStep === 'business_information') {
      const monthlyRevenueField = form.querySelector('input[name="application[monthly_revenue]"]')
      const hasRevenueVerification = monthlyRevenueField && monthlyRevenueField.value && parseFloat(monthlyRevenueField.value) > 0

      if (hasRevenueVerification) {
        const criticalFields = ['application[business_name]', 'application[ein]', 'application[industry]', 'application[years_in_business]']

        criticalFields.forEach(fieldName => {
          const field = form.querySelector(`[name="${fieldName}"]`)
          if (field && field.hasAttribute('required') && !field.value.trim()) {
            field.classList.add('border-red-500')
            this.showFieldError(field, `${this.getFieldLabel(field)} can't be blank`)
            isValid = false
          }
        })
      } else {
        visibleRequiredFields.forEach(field => {
          if (!field.value.trim()) {
            field.classList.add('border-red-500')
            this.showFieldError(field, `${this.getFieldLabel(field)} can't be blank`)
            isValid = false
          }
        })
      }
    } else {
      visibleRequiredFields.forEach(field => {
        if (!field.value.trim()) {
          field.classList.add('border-red-500')
          this.showFieldError(field, `${this.getFieldLabel(field)} can't be blank`)
          isValid = false
        }
      })
    }

    const einField = form.querySelector('input[name="application[ein]"]')
    if (einField && einField.value) {
      const einPattern = /^\d{2}-\d{7}$/
      if (!einPattern.test(einField.value)) {
        einField.classList.add('border-red-500')
        this.showFieldError(einField, 'EIN must be in format XX-XXXXXXX')
        isValid = false
      }
    }

    const emailFields = form.querySelectorAll('input[type="email"]')
    emailFields.forEach(field => {
      if (field.value && !this.isValidEmail(field.value)) {
        field.classList.add('border-red-500')
        this.showFieldError(field, 'Please enter a valid email address')
        isValid = false
      }
    })

    const phoneFields = form.querySelectorAll('input[type="tel"]')
    phoneFields.forEach(field => {
      if (field.value.trim()) {
        let digits = field.value.replace(/\D/g, '')
        if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
        if (digits.length !== 10) {
          field.classList.add('border-red-500')
          this.showFieldError(field, `${this.getFieldLabel(field)} must be a valid 10-digit US phone number`)
          isValid = false
        }
      }
    })

    return isValid
  }

  isValidEmail(email) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(email)
  }

  showValidationMessage() {
    const firstError = this.element.querySelector('.border-red-500')
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      firstError.focus()
    }
  }

  showFieldError(field, message) {
    const container = field.closest('div')
    if (!container) return

    this.clearFieldError(field)

    const errorDiv = document.createElement('div')
    errorDiv.className = 'field-error text-sm text-[#C82659] mb-1'
    errorDiv.textContent = message
    field.parentNode.insertBefore(errorDiv, field)
  }

  clearFieldError(field) {
    const container = field.closest('div')
    if (!container) return

    const existingError = container.querySelector('.field-error')
    if (existingError) {
      existingError.remove()
    }
  }

  clearAllFieldErrors() {
    const errors = this.element.querySelectorAll('.field-error')
    errors.forEach(error => error.remove())
  }

  getFieldLabel(field) {
    const container = field.closest('div')
    const label = container?.querySelector('label')
    if (label) {
      return label.textContent.trim().replace('*', '').trim()
    }
    const name = field.name || field.id || 'This field'
    return name.replace(/.*\[(\w+)\]/, '$1').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  ensureSubmitButtons() {
    this.element.querySelectorAll('input[type="submit"], button[type="submit"]')
  }

  submitForm(event) {
    if (!this.validateFormSilent()) {
      event.preventDefault()
      this.showValidationMessage()
    }
  }

  forceSubmit(event) {
    event.preventDefault()

    if (this.validateFormSilent()) {
      this.element.requestSubmit()
    } else {
      this.showValidationMessage()
    }
  }

  validateFormSilent() {
    return this._performValidation(this.element)
  }

  debugFormSubmission() {
    this.element.addEventListener('submit', (event) => {
      if (event.defaultPrevented) {
        console.error('Form submission was prevented')
      }
    })
  }

  syncMonthlyRevenue(event) {
    const manualInput = event.target
    const hiddenField = this.element.querySelector('#monthly_revenue_field')

    if (hiddenField && manualInput.value) {
      hiddenField.value = manualInput.value
      const changeEvent = new Event('change', { bubbles: true })
      hiddenField.dispatchEvent(changeEvent)
    }
  }
}
